import { useEffect, useState } from "react";
import { api, API_BASE, formatErr } from "@/lib/api";
import { AREAS, CLASSIFICACOES, PESTEL, RECORTES, STATUS, ACCESS_STATUS, labelOf } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { X, ExternalLink, FileText, Check, XCircle, Archive, Trash2, GitBranch, Layers, Sprout, Link2 } from "lucide-react";
import EsteiraMetodologica from "@/components/EsteiraMetodologica";
import ChecklistOperacional from "@/components/ChecklistOperacional";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
const Field = ({ label, value, mono }) => (
  <div>
    <div className="label-mil mb-0.5">{label}</div>
    <div className={`text-sm text-[var(--text)] ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
  </div>
);

const statusBadge = (s) => {
  const def = STATUS.find((x) => x.value === s);
  return <span className={`badge ${def?.className || "badge-muted"}`}>{def?.label || s}</span>;
};

const accessBadge = (s) => {
  if (!s) return <span className="badge badge-muted">—</span>;
  const def = ACCESS_STATUS.find((x) => x.value === s);
  return <span className={`badge ${def?.className || "badge-muted"}`}>{def?.label || s}</span>;
};

export default function SeedDetailModal({ seedId, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [insumo, setInsumo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!seedId) return;
    setData(null); setInsumo(null);
    api.get(`/seeds/${seedId}`)
      .then((r) => {
        setData(r.data);
        const inputId = r.data?.seed?.input_id;
        if (inputId) {
          api.get(`/inputs/${inputId}`)
            .then((ri) => setInsumo(ri.data))
            .catch(() => setInsumo(false));
        }
      })
      .catch((e) => setErr(formatErr(e)));
  }, [seedId]);

  if (!seedId) return null;

  const seed = data?.seed;
  const src = data?.source || {};
  const isAdmin = user?.perfil === "admin" || user?.perfil === "coordenador";
  const isAuthorEditable = user?.user_id === seed?.created_by_user_id && seed?.status_validacao === "pendente";

  const decide = async (action, rejectionReason) => {
    setBusy(true); setErr("");
    try {
      const payload = { action };
      if (rejectionReason) payload.rejection_reason = rejectionReason;
      await api.post(`/seeds/${seed.seed_id}/decision`, payload);
      onChanged?.();
      onClose();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const handleRejectClick = () => {
    const reason = window.prompt("Justifique a rejeição desta semente (obrigatório):", "");
    if (!reason || reason.trim().length < 5) {
      if (reason !== null) setErr("Justificativa muito curta — mínimo 5 caracteres.");
      return;
    }
    decide("rejeitar", reason.trim());
  };

  const handleArchiveClick = () => {
    const note = window.prompt("Observação administrativa para arquivamento (obrigatória):", "");
    if (!note || note.trim().length < 5) {
      if (note !== null) setErr("Observação muito curta — mínimo 5 caracteres.");
      return;
    }
    decideArchive(note.trim());
  };

  const decideArchive = async (note) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/seeds/${seed.seed_id}/decision`, { action: "arquivar", archive_note: note });
      onChanged?.();
      onClose();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Excluir a semente "${seed.titulo_insumo}"? Esta ação é registrada na auditoria.`)) return;
    setBusy(true);
    try {
      await api.delete(`/seeds/${seed.seed_id}`);
      onChanged?.();
      onClose();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="seed-detail-overlay"
    >
      <div
        className="card-mil w-full max-w-5xl my-8 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="seed-detail-modal"
      >
        {/* Header */}
        <div className="p-5 border-b border-[var(--border)] flex items-start justify-between gap-3 gold-accent-top">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              {seed && statusBadge(seed.status_validacao)}
              {seed?.classificacao_semente && <span className="badge badge-gold">{seed.classificacao_semente}</span>}
              {seed?.is_demo && <span className="badge badge-muted" title="Registro de demonstração">DEMO</span>}
              <span className="text-[10px] font-mono text-[var(--text-muted)]">{seed?.seed_id}</span>
            </div>
            <h2 className="font-display font-bold text-xl text-[var(--text)] leading-tight" data-testid="modal-titulo-insumo">
              {seed?.titulo_insumo || "Carregando…"}
            </h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">{src?.titulo || seed?.source_titulo}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--surface-2)] rounded text-[var(--text-muted)] hover:text-[var(--text)]"
            data-testid="modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {!seed ? (
          <div className="p-10 text-center text-[var(--text-muted)] font-mono text-sm">CARREGANDO…</div>
        ) : (
          <div className="overflow-y-auto scrollbar-thin p-6 space-y-6 flex-1">
            {/* 1. Dados principais */}
            <section data-testid="section-dados">
              <h3 className="label-mil text-[var(--gold)] mb-3">Dados Principais</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Título da fonte" value={src.titulo || seed.source_titulo} />
                <Field label="Autores" value={(src.autores || []).join(", ")} />
                <Field label="Instituição / Origem" value={src.origem_fonte || src.instituicao || seed.source_origem} />
                <Field label="Tipo de documento" value={src.tipo_fonte} />
                <Field label="Data da fonte" value={src.data_publicacao || seed.source_data} mono />
                <Field label="Ano" value={src.ano_publicacao} mono />
                <Field label="Idioma" value={src.idioma} />
                <Field label="DOI" value={src.doi} mono />
                <Field label="País de publicação" value={src.pais_publicacao || seed.source_pais} />
                <Field label="País / região de impacto" value={(seed.pais_impacto || []).join(", ")} />
                <Field label="Horizonte temporal" value={seed.horizonte_temporal} mono />
                <div>
                  <div className="label-mil mb-0.5">Link da fonte</div>
                  {(src.link_original || seed.source_link) ? (
                    <a href={src.link_original || seed.source_link} target="_blank" rel="noreferrer"
                       className="text-sm text-[var(--info)] hover:underline flex items-center gap-1 truncate"
                       data-testid="modal-link-fonte">
                      <ExternalLink size={12} /> {src.link_original || seed.source_link}
                    </a>
                  ) : <div className="text-sm text-[var(--text-muted)]">—</div>}
                </div>
                {src.link_drive && (
                  <div>
                    <div className="label-mil mb-0.5">Link Drive</div>
                    <a href={src.link_drive} target="_blank" rel="noreferrer" className="text-sm text-[var(--info)] hover:underline">{src.link_drive}</a>
                  </div>
                )}
                {seed.pdf_filename && (
                  <div>
                    <div className="label-mil mb-0.5">PDF anexo</div>
                    <a href={`${API_BASE}/seeds/${seed.seed_id}/pdf`} target="_blank" rel="noreferrer"
                       className="text-sm text-[var(--gold)] hover:underline flex items-center gap-1"
                       data-testid="modal-pdf-link">
                      <FileText size={12} /> {seed.pdf_filename}
                    </a>
                  </div>
                )}
              </div>
            </section>

            {/* 1.5. Origem Metodológica V2 — Fonte → Insumo → Semente */}
            <section data-testid="section-metodologia-v2" className="border-l-2 border-[var(--gold)] pl-4">
              <h3 className="label-mil text-[var(--gold)] mb-3 flex items-center gap-2">
                <GitBranch size={12} /> Origem Metodológica V2
              </h3>

              {/* Cadeia Fonte → Insumo → Semente */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="cadeia-v2">
                <div className="card-mil p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <FileText size={12} className="text-[var(--info)]" />
                    <span className="label-mil">Fonte</span>
                  </div>
                  <div className="text-xs font-semibold truncate" title={src.titulo}>{src.titulo || seed.source_titulo || "—"}</div>
                  <div className="text-[10px] text-[var(--text-muted)] font-mono truncate mt-1">{seed.source_id || "—"}</div>
                </div>
                <div className="card-mil p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Layers size={12} className="text-[var(--gold)]" />
                    <span className="label-mil">Insumo</span>
                  </div>
                  <div className="text-xs font-semibold truncate" title={insumo?.titulo_insumo}>
                    {insumo?.titulo_insumo
                      || (seed.input_id && insumo === null ? "Carregando…" : null)
                      || (seed.input_id && insumo === false ? "(falha ao carregar insumo)" : null)
                      || (!seed.input_id ? "(não vinculado)" : "—")}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] font-mono truncate mt-1">{seed.input_id || "cadastro direto (sem insumo)"}</div>
                </div>
                <div className="card-mil p-3 gold-accent-top">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sprout size={12} className="text-[var(--success)]" />
                    <span className="label-mil">Semente</span>
                  </div>
                  <div className="text-xs font-semibold truncate">{seed.titulo_insumo}</div>
                  <div className="text-[10px] text-[var(--text-muted)] font-mono truncate mt-1">{seed.seed_id}</div>
                </div>
              </div>

              {/* Acesso à fonte */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="label-mil mb-1">Status de acesso à fonte</div>
                  <div className="flex flex-wrap items-center gap-2">
                    {accessBadge(seed.access_status || src.access_status)}
                    {seed.access_notes && <span className="text-xs text-[var(--text-muted)]">· {seed.access_notes}</span>}
                  </div>
                </div>
                <div>
                  <div className="label-mil mb-1">Documento vinculado</div>
                  {src.pdf_filename ? (
                    <a href={`${API_BASE}/sources/${seed.source_id}/pdf`} target="_blank" rel="noreferrer"
                       className="text-sm text-[var(--gold)] hover:underline flex items-center gap-1"
                       data-testid="link-pdf-fonte">
                      <FileText size={12} /> {src.pdf_filename}
                    </a>
                  ) : seed.pdf_filename ? (
                    <a href={`${API_BASE}/seeds/${seed.seed_id}/pdf`} target="_blank" rel="noreferrer"
                       className="text-sm text-[var(--gold)] hover:underline flex items-center gap-1"
                       data-testid="link-pdf-semente">
                      <FileText size={12} /> {seed.pdf_filename}
                    </a>
                  ) : (
                    <div className="text-xs text-[var(--text-muted)]">Sem PDF anexo</div>
                  )}
                </div>
                {(seed.link_drive_excepcional || src.link_drive) && (
                  <div className="md:col-span-2">
                    <div className="label-mil mb-1 flex items-center gap-1">
                      <Link2 size={10} /> Link Drive {seed.link_drive_excepcional ? "(excepcional)" : ""}
                    </div>
                    <a href={seed.link_drive_excepcional || src.link_drive} target="_blank" rel="noreferrer"
                       className="text-xs text-[var(--info)] hover:underline break-all"
                       data-testid="link-drive">
                      <ExternalLink size={10} className="inline mr-1" />{seed.link_drive_excepcional || src.link_drive}
                    </a>
                  </div>
                )}
                {(seed.pagina_ou_secao || insumo?.pagina_ou_secao) && (
                  <Field label="Página / Seção" value={seed.pagina_ou_secao || insumo?.pagina_ou_secao} mono />
                )}
                {(seed.trecho_traduzido_pt || insumo?.trecho_traduzido_pt) && (
                  <div className="md:col-span-2">
                    <div className="label-mil mb-1">Trecho traduzido (pt-BR)</div>
                    <p className="text-xs text-[var(--text-muted)] italic border-l-2 border-[var(--border)] pl-3">
                      {seed.trecho_traduzido_pt || insumo?.trecho_traduzido_pt}
                    </p>
                  </div>
                )}
              </div>

              {/* Esteira Metodológica 8 etapas (read-only) */}
              <div className="mb-4">
                <EsteiraMetodologica value={seed.methodology_stage || {}} onChange={null} compact />
              </div>

              {/* Checklist Operacional read-only */}
              {seed.researcher_checklist && Object.keys(seed.researcher_checklist).length > 0 && (
                <div data-testid="checklist-readonly">
                  <ChecklistOperacional value={seed.researcher_checklist} onChange={null} readOnly />
                </div>
              )}
              {(!seed.researcher_checklist || Object.keys(seed.researcher_checklist).length === 0) && (
                <div className="text-xs text-[var(--text-muted)] italic">
                  Checklist operacional não preenchido pelo pesquisador.
                </div>
              )}
            </section>

            {/* 2. Classificação prospectiva */}
            <section data-testid="section-classificacao">
              <h3 className="label-mil text-[var(--gold)] mb-3">Classificação Prospectiva</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Área temática principal" value={labelOf(AREAS, seed.area_tematica_principal)} />
                <div>
                  <div className="label-mil mb-0.5">Áreas secundárias</div>
                  <div className="flex flex-wrap gap-1">
                    {(seed.areas_tematicas_secundarias || []).map((a) => (
                      <span key={a} className="badge badge-info">{labelOf(AREAS, a)}</span>
                    ))}
                    {(seed.areas_tematicas_secundarias || []).length === 0 && <span className="text-sm text-[var(--text-muted)]">—</span>}
                  </div>
                </div>
                <Field label="Classificação da semente" value={labelOf(CLASSIFICACOES, seed.classificacao_semente)} />
                <div>
                  <div className="label-mil mb-0.5">Perspectiva PESTEL</div>
                  <div className="flex flex-wrap gap-1">
                    {(seed.perspectiva_pestel || []).map((p) => (
                      <span key={p} className="badge badge-gold">{labelOf(PESTEL, p)}</span>
                    ))}
                    {(seed.perspectiva_pestel || []).length === 0 && <span className="text-sm text-[var(--text-muted)]">—</span>}
                  </div>
                </div>
                <Field label="Recorte geográfico" value={labelOf(RECORTES, seed.recorte_geografico)} />
                <Field label="Impacto a partir de 2030" value={seed.impacto_2030_mais ? "Sim" : "Não"} />
                <Field label="Nível de relevância" value={`${seed.nivel_relevancia ?? 0} / 100`} />
                <Field label="Score confiança IA" value={`${seed.confianca_ia ?? 0} / 100`} />
              </div>
            </section>

            {/* 3. Resumo e conteúdo */}
            <section data-testid="section-resumo">
              <h3 className="label-mil text-[var(--gold)] mb-3">Resumo & Conteúdo</h3>
              <div className="space-y-3">
                {seed.resumo_executivo && (
                  <div>
                    <div className="label-mil mb-1">Resumo executivo</div>
                    <p className="text-sm text-[var(--text)] leading-relaxed bg-[var(--bg)] border border-[var(--border)] rounded p-3">
                      {seed.resumo_executivo}
                    </p>
                  </div>
                )}
                {seed.citacao_direta && (
                  <div>
                    <div className="label-mil mb-1">Citação direta</div>
                    <blockquote className="text-sm italic text-[var(--text-muted)] border-l-2 border-[var(--gold)] pl-3 py-1">
                      {seed.citacao_direta}
                    </blockquote>
                  </div>
                )}
                {seed.justificativa_classificacao && (
                  <div>
                    <div className="label-mil mb-1">Justificativa da classificação</div>
                    <p className="text-sm text-[var(--text)] leading-relaxed">{seed.justificativa_classificacao}</p>
                  </div>
                )}
                {(seed.palavras_chave || []).length > 0 && (
                  <div>
                    <div className="label-mil mb-1">Palavras-chave</div>
                    <div className="flex flex-wrap gap-1">
                      {seed.palavras_chave.map((k) => (
                        <span key={k} className="badge badge-muted">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* 4. Responsabilidade — papéis separados (governança) */}
            <section data-testid="section-autoria">
              <h3 className="label-mil text-[var(--gold)] mb-3">Responsabilidade</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label="Pesquisado por"
                  value={
                    seed.researcher_user_name
                    || seed.pesquisador_responsavel_original
                    || seed.pesquisador_nome
                    || "Não informado"
                  }
                />
                <Field
                  label="Importado por"
                  value={seed.imported_by_user_name || "Não informado"}
                />
                <Field
                  label="Enriquecido por IA"
                  value={seed.enriched_by_user_name || (seed.ai_processed ? seed.created_by_user_name : "Ainda não enriquecido")}
                />
                <Field
                  label="Data do enriquecimento"
                  value={fmtDate(seed.enriched_at || seed.ai_processed_at)}
                  mono
                />
                <Field
                  label="Revisado por"
                  value={seed.reviewed_by_user_name || "Ainda não revisado"}
                />
                <Field
                  label="Data da revisão"
                  value={fmtDate(seed.reviewed_at)}
                  mono
                />
                <Field
                  label="Aprovado por"
                  value={seed.approved_by_user_name || "Ainda não aprovado"}
                />
                <Field
                  label="Data da aprovação"
                  value={fmtDate(seed.approved_at)}
                  mono
                />
                <Field
                  label="Origem do cadastro"
                  value={
                    ({
                      manual: "Manual",
                      upload_ia: "Upload com IA",
                      automatica: "Coleta automática",
                      legada: "Importação legada",
                      legada_enriquecida: "Importação legada (enriquecida por IA)",
                    })[seed.origem_cadastro] || "—"
                  }
                />
                <Field
                  label="Data de inserção"
                  value={fmtDate(seed.created_at || seed.data_cadastro)}
                  mono
                />
                {seed.ai_processed && (
                  <Field label="Score IA" value={`${seed.ai_confidence_score ?? seed.confianca_ia ?? 0} / 100`} mono />
                )}
                {seed.source_file_name && <Field label="Arquivo enviado" value={seed.source_file_name} mono />}
              </div>
              {seed.rejection_reason && (
                <div className="mt-4 border-l-2 border-[var(--danger)] bg-[var(--danger)]/5 p-3 rounded-r">
                  <div className="label-mil text-[var(--danger)] mb-1">Motivo da rejeição</div>
                  <p className="text-sm text-[var(--text)] leading-relaxed">{seed.rejection_reason}</p>
                </div>
              )}
              {seed.archive_note && (
                <div className="mt-4 border-l-2 border-[var(--warn)] bg-[var(--warn)]/5 p-3 rounded-r">
                  <div className="label-mil text-[var(--warn)] mb-1">Observação administrativa (arquivamento)</div>
                  <p className="text-sm text-[var(--text)] leading-relaxed">{seed.archive_note}</p>
                </div>
              )}
            </section>

            {err && <div className="text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded">{err}</div>}
          </div>
        )}

        {/* Footer / Actions */}
        {seed && (
          <div className="p-4 border-t border-[var(--border)] flex flex-wrap items-center justify-end gap-2 bg-[var(--bg)]/40">
            {isAdmin ? (
              <>
                <button onClick={() => decide("aprovar")} disabled={busy || seed.status_validacao === "aprovado"}
                        className="btn-gold text-xs flex items-center gap-1" data-testid="modal-aprovar">
                  <Check size={12} /> Aprovar
                </button>
                <button onClick={handleRejectClick} disabled={busy}
                        className="btn-ghost text-xs flex items-center gap-1 hover:!border-[var(--danger)] hover:!text-[var(--danger)]"
                        data-testid="modal-rejeitar">
                  <XCircle size={12} /> Rejeitar
                </button>
                <button onClick={handleArchiveClick} disabled={busy}
                        className="btn-ghost text-xs flex items-center gap-1" data-testid="modal-arquivar">
                  <Archive size={12} /> Arquivar
                </button>
                <button onClick={remove} disabled={busy}
                        className="btn-ghost text-xs flex items-center gap-1 hover:!border-[var(--danger)] hover:!text-[var(--danger)]"
                        data-testid="modal-excluir">
                  <Trash2 size={12} /> Excluir
                </button>
              </>
            ) : isAuthorEditable ? (
              <div className="text-xs text-[var(--text-muted)] mr-auto">
                Você é o autor; pode editar enquanto estiver pendente.
              </div>
            ) : (
              <div className="text-xs text-[var(--text-muted)] mr-auto">Visualização somente leitura.</div>
            )}
            <button onClick={onClose} className="btn-ghost text-xs" data-testid="modal-fechar">Fechar</button>
          </div>
        )}
      </div>
    </div>
  );
}
